import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

async function compressImage(file, maxBytes = 200 * 1024) {
  if (file.size <= maxBytes) return file;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        const compress = (scale, quality) => {
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (!blob) { resolve(file); return; }
            if (blob.size <= maxBytes) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
            } else if (quality > 0.15) {
              compress(scale, Math.round((quality - 0.1) * 100) / 100);
            } else if (scale > 0.3) {
              compress(scale - 0.1, 0.7);
            } else {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
            }
          }, "image/jpeg", quality);
        };
        compress(1.0, 0.85);
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Derives Valid Upto (Date of Retirement) from DOB + Status.
// W: retires at 60 (must be under 60 already). E: Extended Service, age 60-64,
// renews +1 year from today. R/H: Retired/Emeritus, age 60+; retires at 70 if
// under 70, else +5 years from today. Returns { dor, error } — error is set
// (and dor left null) when the DOB/Status combination is invalid for that status.
function calculateDOR(dobStr, status) {
  if (!dobStr) return { dor: null, error: null };
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return { dor: null, error: null };

  const today = new Date();
  const dobDay = dob.getDate();
  const dobMonth = dob.getMonth();
  const dobYear = dob.getFullYear();

  let age = today.getFullYear() - dobYear;
  const thisYearBirthday = new Date(today.getFullYear(), dobMonth, dobDay);
  if (today < thisYearBirthday) age--;

  let dorDate = null;

  if (status === "W") {
    if (age >= 60) {
      return { dor: null, error: "Employee age is 60 or above. Please select 'Extended Service' or 'Retiree'." };
    }
    const retirementYear = dobYear + 60;
    dorDate = dobDay === 1 ? new Date(retirementYear, dobMonth, 0) : new Date(retirementYear, dobMonth + 1, 0);
  } else if (status === "E") {
    if (age < 60) {
      return { dor: null, error: "Extended Service is allowed only for age 60 and above." };
    }
    if (age >= 65) {
      return { dor: null, error: "Extended Service not allowed beyond age 65." };
    }
    dorDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
  } else if (status === "R" || status === "H") {
    if (age < 60) {
      return { dor: null, error: "Retiree/Emeritus status is allowed only for age 60 and above." };
    }
    if (age < 70) {
      const retirementYear = dobYear + 70;
      dorDate = dobDay === 1 ? new Date(retirementYear, dobMonth, 0) : new Date(retirementYear, dobMonth + 1, 0);
    } else {
      dorDate = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate());
    }
  }

  if (!dorDate) return { dor: null, error: null };

  const yyyy = dorDate.getFullYear();
  const mm = String(dorDate.getMonth() + 1).padStart(2, "0");
  const dd = String(dorDate.getDate()).padStart(2, "0");
  return { dor: `${yyyy}-${mm}-${dd}`, error: null };
}

export default function EmployeeEdit() {
  const { id } = useParams();
  const moduleId = 4;

  // The logged-in user's own moduleId (distinct from the hardcoded `moduleId`
  // constant above, which is a fixed literal used only for API calls).
  const currentUserModuleId = (() => {
    try {
      const raw = localStorage.getItem("user");
      const u = raw ? JSON.parse(raw) : null;
      return parseInt(u?.moduleId, 10);
    } catch {
      return NaN;
    }
  })();

  const [employee, setEmployee] = useState(null);
  const [dorWarning, setDorWarning] = useState("");
  const [nameTitles, setNameTitles] = useState([]);
  const [bloodGroups, setBloodGroups] = useState([]);
  const [castes, setCastes] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [religions, setReligions] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [budgetDepartments, setBudgetDepartments] = useState([]);
  const [resolvers, setResolvers] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Fetch employee data
    fetch(`/api/profile/edit/${id}?moduleId=${moduleId}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch employee");
        }
        return res.json();
      })
      .then((data) => {
        setEmployee(data.employee);
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });

    // Fetch dropdown data (replace with real API endpoints as needed)
    fetch("/api/meta/name-titles").then(res => res.json()).then(data => setNameTitles(Array.isArray(data) ? data : [])).catch(() => setNameTitles([]));
    fetch("/api/meta/designations").then(res => res.json()).then(data => setDesignations(Array.isArray(data) ? data : [])).catch(() => setDesignations([]));
    fetch("/api/meta/departments").then(res => res.json()).then(data => setBudgetDepartments(Array.isArray(data) ? data : [])).catch(() => setBudgetDepartments([]));
    fetch("/api/meta/bloodgroups").then(res => res.json()).then(data => setBloodGroups(data)).catch(() => setBloodGroups([]));
    fetch("/api/meta/castes").then(res => res.json()).then(data => setCastes(data)).catch(() => setCastes([]));
    fetch("/api/meta/qualifications").then(res => res.json()).then(data => setQualifications(data)).catch(() => setQualifications([]));
    fetch("/api/meta/religions").then(res => res.json()).then(data => setReligions(data)).catch(() => setReligions([]));
    fetch("/api/states").then(res => res.json()).then(data => setStates(Array.isArray(data) ? data : [])).catch(() => setStates([]));
  }, [id, moduleId]);

  // Fetch cities whenever PermanentStateID changes (on load or on user change)
  useEffect(() => {
    const stateId = employee?.PermanentStateID;
    if (!stateId) { setCities([]); return; }
    fetch(`/api/cities?stateId=${stateId}`)
      .then(res => res.json())
      .then(data => setCities(Array.isArray(data) ? data : []))
      .catch(() => setCities([]));
  }, [employee?.PermanentStateID]);

  // EResolver options depend on the loaded employee's own EmpType (resolver
  // range differs for Teaching vs Non-Teaching — same rule as EditProfile.jsx),
  // so this can't be fetched until the employee record has loaded.
  useEffect(() => {
    const empType = employee?.EmpType;
    if (empType === undefined) { setResolvers([]); return; }
    fetch(`/api/profile/edit/options?moduleId=${moduleId}&empType=${empType || ""}`)
      .then(res => res.json())
      .then(data => setResolvers(Array.isArray(data?.resolvers) ? data.resolvers : []))
      .catch(() => setResolvers([]));
  }, [employee?.EmpType, moduleId]);

  const handleChange = (field, value) => {
    setEmployee((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("DesignationID", employee.DesignationID || "");
      formData.append("NameTitleID", employee.NameTitleID);
      formData.append("FNameTitleID", employee.FNameTitleID);
      formData.append("FatherName", employee.FatherName);
      formData.append("Gender", employee.Gender);
      formData.append("DOB", employee.DOB);
      formData.append("Email", employee.Email || "");
      formData.append("EMail", employee.Email || "");
      formData.append("PermanentAddress", employee.PermanentAddress || "");
      formData.append("PermanentCityID", employee.PermanentCityID || "");
      formData.append("PermanentStateID", employee.PermanentStateID || "");
      formData.append("PermanentPIN", employee.PermanentPIN || "");
      formData.append("DOR", employee.DOR || "");
      formData.append("MNameTitleID", employee.MNameTitleID || "");
      formData.append("MotherName", employee.MotherName || "");
      formData.append("BloodGroupID", employee.BloodGroupID || "");
      formData.append("EmgMobile", employee.EmgMobile || "");
      formData.append("PAN", employee.PAN || "");
      formData.append("Aadhar", employee.Aadhar || "");
      formData.append("QualificationID", employee.QualificationID || "");
      formData.append("CasteCategoryID", employee.CasteCategoryID || "");
      formData.append("ReligionID", employee.ReligionID || "");
      formData.append("AMobile", employee.AMobile || "");
      formData.append("AEmail", employee.AEmail || "");
      formData.append("BHDepartmentID", employee.BudgetDepartmentID ?? "");
      formData.append("PropertyID", employee.PropertyID);
      formData.append("EResolverID", employee.EResolverID);
      formData.append("DOJ", employee.DOJ);
      formData.append("ECRNo", employee.ECRNo);
      formData.append("MeritRank", employee.MeritRank);
      formData.append("DOC", employee.DOC);
      formData.append("AResolverID", employee.AResolverID);
      formData.append("PFNo", employee.PFNo);
      formData.append("deptid", employee.DepartmentID);
      formData.append("LockFields", employee.LockFields);
      formData.append("Printing", employee.Printing || "");
      formData.append("PUTA", employee.PUTA || "N");
      formData.append("Active", employee.Active);
      formData.append("Status", employee.Status);
      formData.append("EmpType", employee.EmpType);
      formData.append("JobType", employee.JobType);
      if (employee.SupportingDocFile) {
        formData.append("SupportingDoc", employee.SupportingDocFile);
      }
      if (employee.ProfilePicFile) {
        formData.append("ProfilePic", employee.ProfilePicFile);
      }

      const res = await fetch(`/api/profile/edit/${id}?moduleId=${moduleId}`, {
        method: "POST",
        body: formData
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Save failed");
      }

      alert("Record updated successfully");

      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({ type: "EMPLOYEE_EDIT_SAVED" }, window.location.origin);
      }

      window.close();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <h5>Loading...</h5>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4 text-danger">
        {error}
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-4">Edit Employee</h3>

      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-md-3 mb-3">
              <label className="form-label">Active</label>
              <select
                className="form-control"
                value={employee.Active || ""}
                onChange={e => handleChange("Active", e.target.value)}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Employee ID</label>
              <input
                type="text"
                className="form-control"
                value={employee.EmployeeID || ""}
                readOnly
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">PFNo</label>
              <input
                type="text"
                className="form-control"
                value={employee.PFNo || ""}
                readOnly
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">PUTA</label>
              <select
                className="form-control"
                value={employee.PUTA || "N"}
                onChange={e => handleChange("PUTA", e.target.value)}
              >
                <option value="Y">Yes</option>
                <option value="N">No</option>
              </select>
            </div>

          {/* ...existing employee fields... */}

          {/* File upload fields row */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Supporting Document (JPG/PNG, max 200KB — auto-compressed)</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                className="form-control"
                onChange={async (e) => {
                  const raw = e.target.files[0];
                  if (!raw) return;
                  const compressed = await compressImage(raw);
                  setEmployee(prev => ({ ...prev, SupportingDocFile: compressed }));
                }}
              />
              {employee.SupportingDoc && (
                <div className="mt-1">
                  <a href={`/api/profile/supportingdoc/${employee.SupportingDoc}`} target="_blank" rel="noopener noreferrer">View Existing</a>
                </div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Profile Pic (JPG/PNG, max 200KB — auto-compressed)</label>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                className="form-control"
                onChange={async (e) => {
                  const raw = e.target.files[0];
                  if (!raw) return;
                  const compressed = await compressImage(raw);
                  setEmployee(prev => ({ ...prev, ProfilePicFile: compressed }));
                }}
              />
              {employee.ProfilePic && (
                <div className="mt-1">
                  <a href={`https://pumis.puchd.ac.in/Profile/uploads/faces/${employee.ProfilePic}`} target="_blank" rel="noopener noreferrer">View Existing</a>
                </div>
              )}
            </div>
          </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={employee.Status || ""}
                onChange={e => handleChange("Status", e.target.value)}
              >
                <option value="W">Working</option>
                <option value="R">Retired</option>
                <option value="E">Extended Service</option>
                <option value="X">Expired</option>
                <option value="H">Emeritus</option>
                {currentUserModuleId === 4 && <option value="L">Left</option>}
                {currentUserModuleId === 4 && <option value="S">Suspension</option>}
                {currentUserModuleId === 4 && <option value="D">Deputation</option>}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">EmpType</label>
              <select
                className="form-control"
                value={employee.EmpType || ""}
                onChange={e => handleChange("EmpType", e.target.value)}
              >
                <option value="T">Teaching</option>
                <option value="N">NonTeaching</option>
                <option value="A">AcademicStaff</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">JobType</label>
              <select
                className="form-control"
                value={employee.JobType || ""}
                onChange={e => handleChange("JobType", e.target.value)}
              >
                <option value="P">Permanent</option>
                <option value="D">DailyWages</option>
                <option value="C">Contractual</option>
                <option value="V">Visiting</option>
                <option value="G">Guest Faculty</option>
                <option value="O">Outsourced</option>
                <option value="M">MTS</option>
                <option value="T">TemporaryBasis</option>
                <option value="F">Department Fund</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Department</label>
              <input
                type="text"
                className="form-control"
                value={employee.DepartmentName || ""}
                readOnly
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Name Title</label>
              <select
                className="form-control"
                value={employee.NameTitleID || ""}
                onChange={e => handleChange("NameTitleID", e.target.value)}
              >
                <option value="">Select</option>
                {nameTitles.filter(t => t.NameTitleID !== 7).map(t => (
                  <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={employee.EmpName || ""}
                readOnly
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Designation</label>
              <select
                className="form-control"
                value={employee.DesignationID || ""}
                onChange={e => handleChange("DesignationID", e.target.value)}
              >
                <option value="">Select Designation</option>
                {designations.map(d => (
                  <option key={d.DesignationID} value={d.DesignationID}>{d.Designation}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Gender</label>
              <select
                className="form-control"
                value={employee.Gender || ""}
                onChange={(e) =>
                  handleChange("Gender", e.target.value)
                }
              >
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Others</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Father's Name Title</label>
              <select
                className="form-control"
                value={employee.FNameTitleID || ""}
                onChange={e => handleChange("FNameTitleID", e.target.value)}
              >
                <option value="">Select</option>
                {nameTitles.map(t => (
                  <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Father Name</label>
              <input
                type="text"
                className="form-control"
                value={employee.FatherName || ""}
                onChange={(e) =>
                  handleChange("FatherName", e.target.value)
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">DOB</label>
              <input
                type="date"
                className="form-control"
                value={employee.DOB ? employee.DOB.substring(0, 10) : ""}
                onChange={(e) => {
                  const { dor, error: dorErr } = calculateDOR(e.target.value, employee.Status);
                  setDorWarning(dorErr || "");
                  setEmployee((prev) => ({ ...prev, DOB: e.target.value, DOR: dor || prev.DOR }));
                }}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Valid Upto</label>
              <input
                type="date"
                className="form-control"
                value={employee.DOR ? employee.DOR.substring(0, 10) : ""}
                readOnly
              />
              {dorWarning && <div className="text-danger small mt-1">{dorWarning}</div>}
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Email</label>
              <input
                type="text"
                className="form-control"
                value={employee.Email || ""}
                onChange={(e) =>
                  handleChange("Email", e.target.value)
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Emergency Mobile</label>
              <input
                type="text"
                className="form-control"
                value={employee.EmgMobile || ""}
                onChange={(e) =>
                  handleChange("EmgMobile", e.target.value)
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Alternate Mobile</label>
              <input
                type="text"
                className="form-control"
                value={employee.AMobile || ""}
                onChange={(e) =>
                  handleChange("AMobile", e.target.value)
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Aadhar</label>
              <input
                type="text"
                className="form-control"
                value={employee.Aadhar || ""}
                onChange={(e) =>
                  handleChange("Aadhar", e.target.value)
                }
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Permanent Address</label>
              <input
                type="text"
                className="form-control"
                value={employee.PermanentAddress || ""}
                onChange={e => handleChange("PermanentAddress", e.target.value)}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">State</label>
              <select
                className="form-control"
                value={employee.PermanentStateID || ""}
                onChange={e => {
                  handleChange("PermanentStateID", e.target.value);
                  handleChange("PermanentCityID", "");
                }}
              >
                <option value="">Select State</option>
                {states.map(s => (
                  <option key={s.StateID} value={s.StateID}>{s.State}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">City</label>
              <select
                className="form-control"
                value={employee.PermanentCityID || ""}
                onChange={e => handleChange("PermanentCityID", e.target.value)}
              >
                <option value="">Select City</option>
                {cities.map(c => (
                  <option key={c.CityID} value={c.CityID}>{c.City}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">PIN Code</label>
              <input
                type="text"
                className="form-control"
                value={employee.PermanentPIN || ""}
                onChange={(e) =>
                  handleChange("PermanentPIN", e.target.value)
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">PAN</label>
              <input
                type="text"
                className="form-control"
                value={employee.PAN || ""}
                onChange={(e) =>
                  handleChange("PAN", e.target.value)
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Date of Joining</label>
              <input
                type="date"
                className="form-control"
                value={employee.DOJ ? employee.DOJ.substring(0, 10) : ""}
                onChange={e => handleChange("DOJ", e.target.value)}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Confirmation Date</label>
              <input
                type="date"
                className="form-control"
                value={employee.DOC ? employee.DOC.substring(0, 10) : ""}
                onChange={e => handleChange("DOC", e.target.value)}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Blood Group</label>
              <select
                className="form-control"
                value={employee.BloodGroupID || ""}
                onChange={e => handleChange("BloodGroupID", e.target.value)}
              >
                <option value="">Select</option>
                {(Array.isArray(bloodGroups) ? bloodGroups : []).map(bg => (
                  <option key={bg.BloodGroupID} value={bg.BloodGroupID}>{bg.BloodGroup}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Caste</label>
              <select
                className="form-control"
                value={employee.CasteCategoryID || ""}
                onChange={e => handleChange("CasteCategoryID", e.target.value)}
              >
                <option value="">Select</option>
                {(Array.isArray(castes) ? castes : []).map(c => (
                  <option key={c.CasteCategoryID} value={c.CasteCategoryID}>{c.Category || c.CasteCategory}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Qualification</label>
              <select
                className="form-control"
                value={employee.QualificationID || ""}
                onChange={e => handleChange("QualificationID", e.target.value)}
              >
                <option value="">Select</option>
                {(Array.isArray(qualifications) ? qualifications : []).map(q => (
                  <option key={q.QualificationID} value={q.QualificationID}>{q.Qualification}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Religion</label>
              <select
                className="form-control"
                value={employee.ReligionID || ""}
                onChange={e => handleChange("ReligionID", e.target.value)}
              >
                <option value="">Select</option>
                {(Array.isArray(religions) ? religions : []).map(r => (
                  <option key={r.ReligionID} value={r.ReligionID}>{r.Religion}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Lock</label>
              <select
                className="form-control"
                value={employee.LockFields || ""}
                onChange={e => handleChange("LockFields", e.target.value)}
              >
                <option value="N">Unlocked</option>
                <option value="Y">Locked</option>
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Printing Batch</label>
              <input
                type="text"
                className="form-control"
                value={employee.Printing || ""}
                onChange={e => handleChange("Printing", e.target.value)}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">Budget Head Department</label>
              <select
                className="form-control"
                value={employee.BudgetDepartmentID || ""}
                onChange={e => handleChange("BudgetDepartmentID", e.target.value)}
              >
                <option value="">Select</option>
                {budgetDepartments.map(d => (
                  <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label">EResolver</label>
              <select
                className="form-control"
                value={employee.EResolverID || ""}
                onChange={e => handleChange("EResolverID", e.target.value)}
              >
                <option value="">Select</option>
                {resolvers.map(r => (
                  <option key={r.ResolverID} value={r.ResolverID}>{r.OfficialName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>

            <button
              className="btn btn-secondary ms-2"
              onClick={() => window.close()}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}